// #genai Maps backend error codes returned by ev-ecomm-orchestration-service (see SLN-6716
// payments_error_code_constant.go) to translation keys used by the billing-info save banner.
//
// Resolution order applied by getSubmitInfoDetails:
//   1. If the response contains a known errorCode → use the curated FE translation here.
//   2. Else if the response has a backend errorDescription → show it as-is (already
//      user-facing per the orchestration-service contract).
//   3. Else → fall back to the generic billing_information__submit_fail_message.
//
// Adding a new code: add an entry below + add the matching key to the en/fr translation
// JSON. Codes intentionally omitted (e.g. legacy 15843) fall through to the backend message.
const BANK_ACCOUNT_INFO_ERROR_TRANSLATION_KEYS = {
  // Internal request-shape errors. Should not be reachable from the UI; surface a generic
  // retry message so the user has something actionable instead of a developer error.
  15820: 'billing_information__bank_save_unexpected_error', // InvalidContentType
  15821: 'billing_information__bank_save_unexpected_error', // UpdateBankAccountInfoInvalidRequestBody
  15822: 'billing_information__bank_data_missing', // BankDataIsMissing
  15823: 'billing_information__bank_token_missing', // TransientTokenIsMissing
  15824: 'billing_information__bank_token_missing', // AccountSuffixIsMissingOrch
  15828: 'billing_information__bank_save_unexpected_error', // BillPayerIDIsMissing
  15829: 'billing_information__bill_to_missing', // BillToIsMissing

  // Downstream gateway / persistence failures — retryable except orphan (contact support).
  15825: 'billing_information__bank_tokenize_failed', // CreateBankTokenFailed
  15826: 'billing_information__bank_tokenize_failed', // PersistenceFailedAfterTokenize
  15827: 'billing_information__bank_save_failed', // ReportOrdersSaveBankAccountFailed
  15839: 'billing_information__bank_get_failed', // GetBankAccountInfoFailed (load flow)
  15840: 'billing_information__bank_tokenize_failed', // CreateBankTokenTmsFailed (no-debit)

  // Billing-address (BillTo.*) missing — FE validation should catch these client-side, but
  // surface a clear per-field message in the banner if the backend rejects anyway.
  15830: 'billing_information__bank_first_name_required', // BillToFirstNameMissing
  15831: 'billing_information__bank_last_name_required', // BillToLastNameMissing
  15832: 'billing_information__bank_address_required', // BillToAddress1Missing
  15833: 'billing_information__bank_city_required', // BillToLocalityMissing
  15834: 'billing_information__bank_state_required', // BillToAdminAreaMissing
  15835: 'billing_information__bank_zip_required', // BillToPostalCodeMissing
  15836: 'billing_information__bank_country_required', // BillToCountryMissing
  15837: 'billing_information__bank_email_required', // BillToEmailMissing
  15838: 'billing_information__bank_phone_required', // BillToPhoneNumberMissing

  // ACH-default toggle guardrails.
  15841: 'billing_information__ach_default_invalid', // IsACHDefaultMustBeTrue
  15842: 'billing_information__no_active_bank_for_default', // NoActiveBankAccountForDefaultToggle

  // CyberSource normalization rejected the account type at the orchestration boundary.
  15844: 'billing_information__account_type_invalid', // AccountTypeInvalid
};

export const getBankAccountInfoErrorTranslationKey = (errorCode) => (
  BANK_ACCOUNT_INFO_ERROR_TRANSLATION_KEYS[errorCode]
);

// #genai Phase 2 — errorType → FE translation key. The MYEV_UI_INTEGRATION_GUIDE (§4, "Always read
// errorType (not errorMessage) when branching UI behaviour") makes errorType the stable contract,
// while numeric errorCodes remain a legacy fallback. Keyed on the exact strings from §4.1.
// NOTE: BankCompanyNameMissing was removed — the company / legal-entity name is now optional and
// lives on the billing address (§2.1/§4.1: "No BankCompanyNameMissing anymore"), so there is no
// company-required error on the bank flow.
const BANK_ACCOUNT_INFO_ERROR_TYPE_KEYS = {
  AccountTypeInvalid: 'billing_information__account_type_invalid',
  IsACHDefaultMustBeTrue: 'billing_information__ach_default_invalid',
  NoActiveBankAccountForDefaultToggle: 'billing_information__no_active_bank_for_default',
  BillToFirstNameMissing: 'billing_information__bank_first_name_required',
  BillToLastNameMissing: 'billing_information__bank_last_name_required',
  BillToAddress1Missing: 'billing_information__bank_address_required',
  BillToLocalityMissing: 'billing_information__bank_city_required',
  BillToAdminAreaMissing: 'billing_information__bank_state_required',
  BillToPostalCodeMissing: 'billing_information__bank_zip_required',
  BillToCountryMissing: 'billing_information__bank_country_required',
  BillToEmailMissing: 'billing_information__bank_email_required',
  BillToPhoneNumberMissing: 'billing_information__bank_phone_required',
  CreateBankTokenFailed: 'billing_information__bank_tokenize_failed',
  CreateBankTokenTmsFailed: 'billing_information__bank_tokenize_failed',
  PersistenceFailedAfterTokenize: 'billing_information__bank_tokenize_failed',
  ReportOrdersSaveBankAccountFailed: 'billing_information__bank_save_failed',
};

export const getBankAccountInfoErrorTypeKey = (errorType) => (
  BANK_ACCOUNT_INFO_ERROR_TYPE_KEYS[errorType]
);

// #genai Phase 2 Add-Payment pages (both AddAchPaymentMethod and AddCardPaymentMethod) show a
// dedicated "Payment method already exists" dialog (Figma 732:26114 / 732:26388) instead of the
// generic "Something went wrong" one when the backend rejects the save because the exact bank /
// card is already saved on the account.
// #genai Populate these sets with the ev-ecomm-orchestration-service duplicate error identifiers
// once the backend contract is finalised (tracked in ticket for backend follow-up). Until then
// the switch always falls through to the generic dialog, matching pre-Phase-2 behaviour.
const DUPLICATE_PAYMENT_METHOD_ERROR_CODES = new Set([
  // 15xxx, // e.g. BankAccountAlreadyExists / CardAlreadyExists — add when the BE codes are confirmed
]);
const DUPLICATE_PAYMENT_METHOD_ERROR_TYPES = new Set([
  // 'BankAccountAlreadyExists', 'CardAlreadyExists' — add when the BE contract lands
]);

// #genai Bugbot "Phase 2 phone-required contract mismatch": PHASE2_REQUIRED_ADDRESS_RULES leaves
// phone optional (Figma marks it optional), but the request body still sends billTo.phoneNumber
// and the orchestration layer maps BillToPhoneNumberMissing (and every other BillToXxxMissing) to
// a per-field required error. Without the mapping below, those backend rejections would fall
// through to the generic "Something went wrong" dialog on the Phase 2 Add-Payment pages, hiding
// which field the backend actually rejected. This map lets the caller route the backend's
// billTo.* error onto the SAME inline addressInfoErrors slice per-keystroke validation writes to,
// so the user sees an asterisk-red field label + helper text + validation banner instead of the
// generic modal. Kept as a separate table so we can bind by errorType (§4 stable contract) or by
// numeric errorCode (legacy fallback) — mirrors the BANK_ACCOUNT_INFO_ERROR_TYPE_KEYS pattern.
const BILL_TO_INLINE_ADDRESS_FIELDS = {
  BillToAddress1Missing: { fieldId: 'address1', translationKey: 'address_information__address1_required' },
  BillToLocalityMissing: { fieldId: 'city', translationKey: 'address_information__city_required' },
  BillToAdminAreaMissing: { fieldId: 'state', translationKey: 'address_information__state_required' },
  BillToPostalCodeMissing: { fieldId: 'zip', translationKey: 'address_information__zip_required' },
  BillToCountryMissing: { fieldId: 'country', translationKey: 'address_information__country_required' },
  BillToEmailMissing: { fieldId: 'emailAddress', translationKey: 'address_information__email_required' },
  BillToPhoneNumberMissing: { fieldId: 'phone', translationKey: 'address_information__phone_required' },
};

// #genai Numeric fallback for older responses that only carry an errorCode (see the legacy 158xx
// range in BANK_ACCOUNT_INFO_ERROR_TRANSLATION_KEYS above). Keeps the errorType path canonical.
const BILL_TO_INLINE_ADDRESS_FIELDS_BY_CODE = {
  15832: BILL_TO_INLINE_ADDRESS_FIELDS.BillToAddress1Missing,
  15833: BILL_TO_INLINE_ADDRESS_FIELDS.BillToLocalityMissing,
  15834: BILL_TO_INLINE_ADDRESS_FIELDS.BillToAdminAreaMissing,
  15835: BILL_TO_INLINE_ADDRESS_FIELDS.BillToPostalCodeMissing,
  15836: BILL_TO_INLINE_ADDRESS_FIELDS.BillToCountryMissing,
  15837: BILL_TO_INLINE_ADDRESS_FIELDS.BillToEmailMissing,
  15838: BILL_TO_INLINE_ADDRESS_FIELDS.BillToPhoneNumberMissing,
};

// #genai Public helper for the Phase 2 Add-Payment pages. Returns the descriptor for surfacing a
// backend billTo.* required error as an inline field error on the shared AddressInformation form:
//   { fieldId: 'phone', fieldError: '<inline helper text>', bannerError: '<validation-banner text>' }
// Returns null when submitInfo does not carry a mappable billTo.* error, so callers can fall back
// to the generic dialog path. `t` is passed in (rather than importing i18n here) to keep this
// module pure — mirrors how getSubmitInfoDetails is wired in BillingInformation.component.js.
export const getBillToInlineFieldError = (submitInfo, t) => {
  if (!submitInfo?.errorMessage) return null;
  const byType = submitInfo.errorType && BILL_TO_INLINE_ADDRESS_FIELDS[submitInfo.errorType];
  const byCode = submitInfo.errorCode && BILL_TO_INLINE_ADDRESS_FIELDS_BY_CODE[submitInfo.errorCode];
  const descriptor = byType || byCode;
  if (!descriptor) return null;
  return {
    fieldId: descriptor.fieldId,
    // #genai The field-level helper text under the input uses the generic "field required" copy
    // the per-keystroke validator already writes for empty required fields (see
    // validateFieldAndHandleChange in AddressInformation) so the two error-source paths look
    // identical to the user. The banner-line item uses the field-specific translation so the
    // ValidationMessageBanner at the top of the form can identify which field to fix.
    fieldError: t('address_information__field_required'),
    bannerError: t(descriptor.translationKey),
  };
};

// #genai Bugbot "BillToFirstNameMissing / BillToLastNameMissing fall through to generic dialog":
// unlike every other BillTo.* field the backend rejects, First / Last name on the Phase 2 Add-ACH
// and Add-Card pages live on the payment-info slice (achPaymentInfo / cardPaymentInfo), NOT on
// addressInfo — the Figma redesign moved them into the "Billing Information" section but they
// stayed wired to the payment-info state that the CyberSource microform reads. So the address
// mapping above (BILL_TO_INLINE_ADDRESS_FIELDS) cannot cover them: dispatching
// updateAddressInfoErrors('firstName', ...) would write to a field the inputs never read.
// This second map targets the payment-info slice — callers dispatch updateAchPaymentInfoErrors /
// updateCardPaymentInfoErrors so the inline helper text + validation-banner line item render on
// the exact First / Last name inputs the user sees. Kept as a separate table (not merged with
// BILL_TO_INLINE_ADDRESS_FIELDS) so each caller can pick the correct dispatch action based on
// the descriptor's source table.
const BILL_TO_INLINE_PAYMENT_FIELDS = {
  BillToFirstNameMissing: { fieldId: 'firstName', translationKey: 'payment_information__first_name_required' },
  BillToLastNameMissing: { fieldId: 'lastName', translationKey: 'payment_information__last_name_required' },
};

// #genai Numeric fallback mirroring BILL_TO_INLINE_ADDRESS_FIELDS_BY_CODE — the legacy 158xx
// range in BANK_ACCOUNT_INFO_ERROR_TRANSLATION_KEYS. Keeps the errorType path canonical.
const BILL_TO_INLINE_PAYMENT_FIELDS_BY_CODE = {
  15830: BILL_TO_INLINE_PAYMENT_FIELDS.BillToFirstNameMissing,
  15831: BILL_TO_INLINE_PAYMENT_FIELDS.BillToLastNameMissing,
};

// #genai Public helper for the Phase 2 Add-Payment pages — parallels getBillToInlineFieldError
// but returns the descriptor for the payment-info slice (First / Last name):
//   { fieldId: 'firstName', fieldError: '<inline text>', bannerError: '<banner text>' }
// #genai Inline subtext is generic "This field is required"; banner keeps the field-specific
// name (Phase 2 Add ACH / Add Card UX feedback). Returns null when submitInfo does not
// carry a mappable First / Last name billTo.* error.
export const getBillToInlinePaymentFieldError = (submitInfo, t) => {
  if (!submitInfo?.errorMessage) return null;
  const byType = submitInfo.errorType && BILL_TO_INLINE_PAYMENT_FIELDS[submitInfo.errorType];
  const byCode = submitInfo.errorCode && BILL_TO_INLINE_PAYMENT_FIELDS_BY_CODE[submitInfo.errorCode];
  const descriptor = byType || byCode;
  if (!descriptor) return null;
  return {
    fieldId: descriptor.fieldId,
    fieldError: t('payment_information__ach_fields_error'),
    bannerError: t(descriptor.translationKey),
  };
};

// #genai Returns which submit-error dialog the Phase 2 Add-Payment pages (ACH + Card) should
// render for the given backend error: 'duplicate' for the "Payment method already exists" modal,
// 'orphan' for the 503 PersistenceFailedAfterTokenize case (§4.1 — instrument verified upstream
// but not persisted; show the supportReference), 'generic' for the "Something went wrong"
// fallback, or null when the caller has no active errorMessage or when the error is a billTo.*
// per-field required error (those get surfaced inline via getBillToInlineFieldError above — the
// generic modal would hide which field the backend actually rejected). Branches on errorType per
// the guide (§4, "Always read errorType (not errorMessage) when branching UI behaviour").
export const getSubmitErrorDialogKind = (submitInfo) => {
  if (!submitInfo?.errorMessage) return null;
  if (DUPLICATE_PAYMENT_METHOD_ERROR_CODES.has(submitInfo.errorCode)) return 'duplicate';
  if (DUPLICATE_PAYMENT_METHOD_ERROR_TYPES.has(submitInfo.errorType)) return 'duplicate';
  if (submitInfo.errorType === 'PersistenceFailedAfterTokenize') return 'orphan';
  // #genai Bugbot fix: hand billTo.* per-field required errors off to the inline field-error
  // path (Add-Ach / Add-Card watch submitInfo and dispatch updateAddressInfoErrors OR
  // updateAch/CardPaymentInfoErrors when this returns null but the error is a mapped billTo.*
  // code). Falling through to 'generic' here would surface the wrong UX (opaque modal instead
  // of a targeted red field). Two separate maps: address fields (email/company/country/...) and
  // payment fields (firstName/lastName — moved to the "Billing Information" section on the
  // Figma redesign but wired to the payment-info state slice the microform owns).
  if (submitInfo.errorType && BILL_TO_INLINE_ADDRESS_FIELDS[submitInfo.errorType]) return null;
  if (submitInfo.errorCode && BILL_TO_INLINE_ADDRESS_FIELDS_BY_CODE[submitInfo.errorCode]) return null;
  if (submitInfo.errorType && BILL_TO_INLINE_PAYMENT_FIELDS[submitInfo.errorType]) return null;
  if (submitInfo.errorCode && BILL_TO_INLINE_PAYMENT_FIELDS_BY_CODE[submitInfo.errorCode]) return null;
  return 'generic';
};

export default BANK_ACCOUNT_INFO_ERROR_TRANSLATION_KEYS;
